import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
import { AuthGuard } from "src/auth/guard";
import MainLayout from "src/layouts/main";
import SimpleLayout from "src/layouts/simple";
import CompactLayout from "src/layouts/compact";

import { SplashScreen } from "src/components/loading-screen";
import HomePricing from "src/sections/home/home-pricing";
import LoginPage from "src/pages/auth/jwt/login";
import AssociationPortalRoute from "src/routes/components/association-portal-route";

// ----------------------------------------------------------------------

export const HomePage = lazy(() => import("src/pages/home"));
const Page500 = lazy(() => import("src/pages/500"));
const Page403 = lazy(() => import("src/pages/403"));
const Page404 = lazy(() => import("src/pages/404"));
const PublicPlayerPage = lazy(() => import("src/pages/federation-public-player"));
const IntroductionPage = lazy(() => import("src/pages/introduction"));
const JoinProviderPage = lazy(() => import("src/pages/join-provider"));

const SportmatesPage = lazy(() => import("src/pages/sportmates/list"));
const SportmateAdDetailsPage = lazy(
  () => import("src/pages/sportmates/details"),
);
const SportmateAdCreatePage = lazy(
  () => import("src/sections/sportmates/view/sportmates-create-view"),
);
const SportmateAdEditPage = lazy(
  () => import("src/sections/sportmates/view/sportmates-edit-view"),
);
const SportmateMyApplications = lazy(
  () => import("src/sections/sportmates/view/sportmates-my-applications-view"),
);
const SportmateRankings = lazy(
  () => import("src/sections/sportmates/view/sportmates-rankings-view"),
);

const SportmateLanding = lazy(
  () => import("src/sections/sportmates/landing/view/sportmates-landing-view"),
);

const SportmateSuccessfulSubscription = lazy(
  () =>
    import(
      "src/sections/sportmates/view/sportmates-successful-subscription-view"
    ),
);

const PartnerCoachPage = lazy(() => import("src/pages/partner-coach"));
const PartnerClubandcourtPage = lazy(
  () => import("src/pages/partner-clubandcourt"),
);
const PartnerTournamentPage = lazy(
  () => import("src/pages/partner-tournament-organiser"),
);
const PartnerDisabledPeopleProgramPage = lazy(
  () => import("src/pages/partner-disabled-people-propgram"),
);

const PartnerPlayerPage = lazy(() => import("src/pages/partner-player"));
const PublicTournamentsPage = lazy(() => import("src/pages/tournaments"));
const SportCommunitiesPage = lazy(() => import("src/pages/sport-communities"));
const SportprovidersPage = lazy(() => import("src/pages/timebookings"));
const FaqsPage = lazy(() => import("src/pages/faqs"));
const AboutPage = lazy(() => import("src/pages/about-us"));
const ContactPage = lazy(() => import("src/pages/contact-us"));
const CodeOfEthicsPage = lazy(() => import("src/pages/code-of-ethics"));
const TermsAndConditionsPage = lazy(
  () => import("src/pages/terms-and-conditions"),
);
const PrivacyPolicyPage = lazy(() => import("src/pages/privacy-policy"));

const ComingSoonPage = lazy(() => import("src/pages/coming-soon"));
const MaintenancePage = lazy(() => import("src/pages/maintenance"));

const CheckoutPage = lazy(() => import("src/pages/checkout"));

const FederationMovementPage = lazy(() => import("src/sections/federation-transfers/movement-view"));
const FederationFinancePage = lazy(() => import("src/sections/federation-finance/finance-view"));
const FederationPlayerPage = lazy(() => import("src/sections/federation-players/player-registration-alias"));
const AssociationMyFeesPage = lazy(
  () => import("src/sections/association/my-fees"),
);
const AssociationRegistrationPage = lazy(
  () =>
    import(
      "src/sections/federation-organisations/association-registration-view"
    ),
);

const PlayerPage = lazy(() => import("src/pages/player/index"));
const TournamentPage = lazy(() => import("src/pages/tournament/index"));
const SportProviderPage = lazy(() => import("src/pages/sportprovider/index"));

// PRODUCT
const ProductListPage = lazy(() => import("src/pages/product/list"));
const ProductDetailsPage = lazy(() => import("src/pages/product/details"));
//const ProductCheckoutPage = lazy(() => import("src/pages/product/checkout"));
// BLOG
const PostListPage = lazy(() => import("src/pages/post/list"));
const PostDetailsPage = lazy(() => import("src/pages/post/details"));

//ADDED COMPONENTS PAGE
const CustomComponentsPage = lazy(() => import("src/pages/custom-components"));
const FeedbackPage = lazy(() => import("src/pages/feedback"));
const TokenUsagePage = lazy(() => import("src/pages/tokenusage"));
const InvitationPage = lazy(() => import("src/pages/invitation"));
const JoinTeamPage = lazy(() => import("src/pages/join-team"));
const TrainingPackagePage = lazy(() => import("src/pages/training-package"));
const CustomApplicationPage = lazy(
  () => import("src/pages/custom-application"),
);
const CustomTeamListPage = lazy(() => import("src/pages/custom-team-list"));

const CountiesAndProviders = lazy(
  () => import("src/pages/counties-and-providers"),
);

// ----------------------------------------------------------------------

export const mainRoutes = [
  {
    element: (
      <MainLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        path: "/custom-components",
        element: <CustomComponentsPage />,
      },
      { path: "login", element: <LoginPage /> },
      { path: "checkout", element: <CheckoutPage /> },
      { path: "introduction", element: <IntroductionPage /> },
      { path: "feedback", element: <FeedbackPage /> },
      { path: "tokenusage", element: <TokenUsagePage /> },
      { path: "invitations", element: <InvitationPage /> },
      { path: "join-team", element: <JoinTeamPage /> },
      { path: "join-provider", element: <JoinProviderPage /> },

      { path: "training-package", element: <TrainingPackagePage /> },
      { path: "counties-and-providers", element: <CountiesAndProviders /> },

      // { path: "sportmates", element: <SportmatesPage /> },
      {
        path: "sportmates",
        children: [
          { element: <SportmatesPage />, index: true },
          // { path: "list", element: <PostListPage /> },
          { path: ":id", element: <SportmateAdDetailsPage /> },
          { path: "create", element: <SportmateAdCreatePage /> },
          { path: "myapplications", element: <SportmateMyApplications /> },
          { path: "edit/:id", element: <SportmateAdEditPage /> },
          { path: "rankings/:sport?", element: <SportmateRankings /> },
          {
            path: "successful-subscription",
            element: <SportmateSuccessfulSubscription />,
          },
          // { path: "landing", element: <SportmateLanding /> },
        ],
      },

      { path: "partner-coach", element: <PartnerCoachPage /> },
      { path: "partner-clubandcourt", element: <PartnerClubandcourtPage /> },
      { path: "partner-player", element: <PartnerPlayerPage /> },

      {
        path: "partner-tournament-organiser",
        element: <PartnerTournamentPage />,
      },
      {
        path: "partner-disabled-people-program",
        element: <PartnerDisabledPeopleProgramPage />,
      },

      {
        path: "tournaments",
        element: (
          <AssociationPortalRoute
            tab="sportprovidermenu_tournaments"
            fallback={<PublicTournamentsPage />}
          />
        ),
      },
      { path: "sport-communities", element: <SportCommunitiesPage /> },
      { path: "players/:playerId", element: <PublicPlayerPage /> },
      {
        path: "profile",
        element: (
          <AssociationPortalRoute tab="sportprovidermenu_profile" welcome />
        ),
      },
      {
        path: "rankings",
        element: <AssociationPortalRoute tab="sportprovidermenu_rankings" />,
      },
      {
        path: "news",
        element: <AssociationPortalRoute tab="sportprovidermenu_newsfeed" />,
      },
      {
        path: "live",
        element: (
          <AssociationPortalRoute tab="sportprovidermenu_livestreaming" />
        ),
      },
      {
        path: "rules",
        element: <AssociationPortalRoute tab="sportprovidermenu_rules" />,
      },
      {
        path: "register",
        element: (
          <AssociationPortalRoute
            tab="sportprovidermenu_profile"
            registration
            fallback={<SportprovidersPage />}
          />
        ),
      },
      //{ path: "timebooking", element: <TimebookingPage /> },
      { path: "sportproviders", element: <SportprovidersPage /> },
      { path: "about-us", element: <AboutPage /> },
      { path: "contact-us", element: <ContactPage /> },
      { path: "faqs", element: <FaqsPage /> },
      { path: "code-of-ethics", element: <CodeOfEthicsPage /> },
      { path: "privacy-policy", element: <PrivacyPolicyPage /> },
      { path: "terms-and-conditions", element: <TermsAndConditionsPage /> },

      {
        path: "sportprovider/:provider/tournaments",
        children: [
          {
            index: true,
            element: <SportProviderPage tab="sportprovidermenu_tournaments" />, // ← ranglista / versenylista
          },
          {
            path: ":id/profile",
            element: <TournamentPage tab="tournamentmenu_profile" />,
          },
          {
            path: ":id/schedule",
            element: <TournamentPage tab="tournamentmenu_schedule" />,
          },
          {
            path: ":id/application",
            element: <TournamentPage tab="tournamentmenu_application" />,
          },
          {
            path: ":id/results",
            element: <TournamentPage tab="tournamentmenu_results" />,
          },
          {
            path: ":id/participants",
            element: <TournamentPage tab="tournamentmenu_participants" />,
          },
          {
            path: ":id/rules",
            element: <TournamentPage tab="tournamentmenu_rules" />,
          },
        ],
      },

      {
        path: "sportprovider",
        children: [
          {
            path: ":id/profile",
            element: (
              <AssociationPortalRoute
                tab="sportprovidermenu_profile"
                welcome
                fallback={<SportProviderPage tab="sportprovidermenu_profile" />}
              />
            ),
          },
          {
            path: ":id/register",
            element: (
              <AssociationPortalRoute
                tab="sportprovidermenu_profile"
                registration
              />
            ),
          },
          {
            path: ":id/calendar",
            element: <SportProviderPage tab="sportprovidermenu_calendar" />,
          },
          {
            path: ":id/calendar/:resourceId",
            element: <SportProviderPage tab="sportprovidermenu_calendar" />,
          },
          {
            path: ":id/trainings",
            element: <SportProviderPage tab="sportprovidermenu_trainings" />,
          },
          {
            path: ":id/venues",
            element: <SportProviderPage tab="sportprovidermenu_venues" />,
          },
          {
            path: ":id/results",
            element: <SportProviderPage tab="sportprovidermenu_results" />,
          },
          {
            path: ":id/rankings",
            element: <SportProviderPage tab="sportprovidermenu_rankings" />,
          },
          { path: ":id/players/:playerId", element: <PublicPlayerPage /> },

          {
            path: ":id/passes",
            element: <SportProviderPage tab="sportprovidermenu_passes" />,
          },
          {
            path: ":id/tickets",
            element: <SportProviderPage tab="sportprovidermenu_tickets" />,
          },
          {
            path: ":id/camps",
            element: <SportProviderPage tab="sportprovidermenu_camps" />,
          },
          {
            path: ":id/rules",
            element: <SportProviderPage tab="sportprovidermenu_rules" />,
          },
          {
            path: ":id/prices",
            element: <SportProviderPage tab="sportprovidermenu_prices" />,
          },
          {
            path: ":id/newsfeed",
            element: <SportProviderPage tab="sportprovidermenu_newsfeed" />,
          },
          {
            path: ":id/tournaments",
            element: <SportProviderPage tab="sportprovidermenu_tournaments" />,
          },
          {
            path: ":id/championships",
            element: (
              <SportProviderPage tab="sportprovidermenu_championships" />
            ),
          },
          {
            path: ":id/clubs",
            element: <SportProviderPage tab="sportcommunitymenu_clubs" />,
          },
          {
            path: ":id/rankings",
            element: <SportProviderPage tab="sportprovidermenu_rankings" />,
          },
          {
            path: ":id/livecalendar",
            element: <SportProviderPage tab="sportprovidermenu_livecalendar" />,
          },
          {
            path: ":id/livecalendar/:resourceId",
            element: <SportProviderPage tab="sportprovidermenu_livecalendar" />,
          },
          {
            path: ":id/livestreaming",
            element: (
              <SportProviderPage tab="sportprovidermenu_livestreaming" />
            ),
          },
        ],
      },
    ],
  },

  {
    element: (
      <AuthGuard>
        <MainLayout>
          <Suspense fallback={<SplashScreen />}>
            <Outlet />
          </Suspense>
        </MainLayout>
      </AuthGuard>
    ),
    children: [
      {
        path: "player",
        children: [
          { path: "federation-registration", element: <FederationPlayerPage /> },
          { path: "association-fees", element: <AssociationMyFeesPage /> },
          { path: "federation-obligations", element: <FederationFinancePage mode="player" /> },
          { path: "federation-transfers", element: <FederationMovementPage mode="player" /> },
          {
            path: "association-registration",
            element: <AssociationRegistrationPage />,
          },
          {
            path: "overview",
            element: <PlayerPage tab="playermenu_overview" />,
          },
          {
            path: "profiledata",
            element: <PlayerPage tab="playermenu_profiledata" />,
          },
          {
            path: "activities",
            element: <PlayerPage tab="playermenu_myapplications" />,
          },
          {
            path: "passes-tickets",
            element: <PlayerPage tab="playermenu_passestickets" />,
          },

          {
            path: "teams",
            element: <PlayerPage tab="playermenu_teams" />,
          },

          {
            path: "messages",
            element: <PlayerPage tab="playermenu_messages" />,
          },
          { path: "results", element: <PlayerPage tab="playermenu_results" /> },

          {
            path: "providers",
            element: <PlayerPage tab="playermenu_providers" />,
          },
        ],
      },
    ],
  },
  {
    element: (
      <MainLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </MainLayout>
    ),
    children: [
      {
        path: "product",
        children: [
          { element: <ProductListPage />, index: true },
          { path: "list", element: <ProductListPage /> },
          { path: ":id", element: <ProductDetailsPage /> },
          // { path: "checkout", element: <ProductCheckoutPage /> },
        ],
      },

      {
        path: "post",
        children: [
          { element: <PostListPage />, index: true },
          { path: "list", element: <PostListPage /> },
          { path: ":title", element: <PostDetailsPage /> },
        ],
      },
    ],
  },
  {
    element: (
      <SimpleLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </SimpleLayout>
    ),
    children: [
      {
        path: "sportmates",
        children: [{ path: "landing", element: <SportmateLanding /> }],
      },
      { path: "pricing", element: <HomePricing /> },
      // {
      //   path: "custom-application/:provider?",
      //   element: <CustomApplicationPage />,
      // },
      // {
      //   path: "custom-team-list/:provider?/:tournamentId",
      //   element: <CustomTeamListPage />,
      // },

      // { path: "pricing", element: <PricingPage /> },
      // { path: "payment", element: <PaymentPage /> },
    ],
  },
  {
    element: (
      <SimpleLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </SimpleLayout>
    ),
    children: [
      {
        path: "custom-application/:provider?",
        element: <CustomApplicationPage />,
      },
      {
        path: "custom-team-list/:provider?/:tournamentId",
        element: <CustomTeamListPage />,
      },

      // { path: "pricing", element: <PricingPage /> },
      // { path: "payment", element: <PaymentPage /> },
    ],
  },

  {
    element: (
      <CompactLayout>
        <Suspense fallback={<SplashScreen />}>
          <Outlet />
        </Suspense>
      </CompactLayout>
    ),
    children: [
      { path: "coming-soon", element: <ComingSoonPage /> },
      { path: "maintenance", element: <MaintenancePage /> },
      { path: "500", element: <Page500 /> },
      { path: "404", element: <Page404 /> },
      { path: "403", element: <Page403 /> },
    ],
  },
];
